import { Injectable } from '@angular/core';
import type { ApplicationInsights, IConfig, IConfiguration } from '@microsoft/applicationinsights-web';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class LogService {
  // Application Insights is ~190 kB, so it's loaded in a separate chunk after start instead of in the initial bundle.
  // Calls made before it has loaded wait for it; if it fails to load, logging is silently skipped.
  private ready: Promise<ApplicationInsights | undefined>;

  constructor() {
    this.ready = import('@microsoft/applicationinsights-web')
      .then(({ ApplicationInsights }) => {
        const appInsights = new ApplicationInsights({
          config: {
            connectionString: environment.appInsights.connectionString,
            disableFetchTracking: true,
            enableAutoRouteTracking: false,
            // Chrome is phasing out the 'unload' event; the SDK still flushes on 'beforeunload' and 'pagehide'
            disablePageUnloadEvents: ['unload'],
            // no cookies or local/session storage: telemetry storage would need consent (LEK 9:28), so only necessary storage is used
            cookieCfg: {
              enabled: false,
            },
            isStorageUseDisabled: true,
          } as IConfiguration & IConfig,
        });
        appInsights.addTelemetryInitializer(item => {
          item.tags!['ai.cloud.roleInstance'] = environment.appInsights.cloudRoleInstance;
        });
        appInsights.loadAppInsights();
        return appInsights;
      })
      .catch(() => undefined);
  }

  logPageView(name?: string, url?: string) {
    this.ready.then(appInsights =>
      appInsights?.trackPageView({
        name: name,
        uri: url,
      })
    );
  }

  logEvent(name: string, properties?: Record<string, unknown>) {
    this.ready.then(appInsights => appInsights?.trackEvent({ name: name }, properties));
  }

  logMetric(name: string, average: number, properties?: Record<string, unknown>) {
    this.ready.then(appInsights => appInsights?.trackMetric({ name: name, average: average }, properties));
  }

  logException(exception: Error, properties?: Record<string, unknown>) {
    this.ready.then(appInsights =>
      appInsights?.trackException(
        {
          exception: exception,
        },
        properties
      )
    );
  }

  logTrace(message: string, properties?: Record<string, unknown>) {
    this.ready.then(appInsights => appInsights?.trackTrace({ message: message }, properties));
  }
}
